<template>
  <div class="row">
    <!-- Spinner ładowania (pokazuje się, gdy loading = true) -->
    <spinner-element :loading="loading" />
    <div class="row">
      <div class="col-12" id="details">
        <!-- Sekcja z danymi o grupach VMS -->
        <div class="row" id="road-info" v-if="!loading">
          <!-- Dla każdej grupy VMS (np. oddzielne odcinki dróg) -->
          <div v-for="(group, groupId) in visibleGroups" :key="groupId" class="content">
            <!-- Pasek tytułowy grupy oraz przycisk rozwiń/zwiń.
                 `mb-2` tylko w stanie zwiniętym (przycisk pokazuje „Rozwiń") —
                 oddziela wtedy kolejne grupy od siebie. Gdy grupa jest
                 rozwinięta (przycisk „Zwiń"), pasek ma przylegać do tabeli,
                 więc margines znika. -->
            <div
              class="d-flex justify-content-between align-items-center table-container"
              :class="{ 'mb-2': isExpanded(groupId) }"
            >
              <div class="group-heading">
                <h5>{{ descriptions[groupId] }}</h5>
                <!-- Metka czasu mówi, jak świeży jest prezentowany stan. Stan pochodzi ze snapshotu
                     zbieranego w tle, więc bez niej operator nie wiedziałby, na co patrzy. -->
                <span class="group-meta" v-if="checkedAtFor(groupId)">
                  {{ t('vms-status-checked-at', { time: formatTime(checkedAtFor(groupId)) }) }}
                </span>
                <span class="group-meta" v-if="lastLogLabel(groupId)">
                  {{ t('vms-last-operation') }}: {{ lastLogLabel(groupId) }}
                </span>
              </div>
              <button @click="toggleGroup(groupId)" class="btn-download">
                {{ isExpanded(groupId) ? t('collapse') : t('expand') }}
              </button>
            </div>
            <!-- Rozwinięcie grupy: Tabela ze znakami -->
            <div v-if="isExpanded(groupId)">
              <table class="center-aligned table table-bordered">
                <tbody>
                  <tr>
                    <!-- Tabela z listą znaków VMS -->
                    <td>
                      <table class="center-aligned table table-striped" :class="{ 'with-service-control': isServiceUser }">
                        <thead>
                          <tr>
                            <th class="lp">{{ t('ordinal-number') }}</th>
                            <th class="road_no">{{ t('short-road-no') }}</th>
                            <th class="chainage">{{ t('chainage') }}</th>
                            <th class="ip_address">{{ t('ip-address') }}</th>
                            <th class="status">{{ t('status') }}</th>
                            <th class="sign_enabled">{{ t('sign-enabled') }}</th>
                            <th class="sign_image">{{ t('sign-image') }}</th>
                            <th class="variant">{{ t('selected-variant') }}</th>
                            <th class="service_control" v-if="isServiceUser">{{ t('vms-service-control') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Dla każdego znaku w grupie -->
                          <tr v-for="(vms, vmsIndex) in group" :key="vms.vms.id">
                            <td>{{ vmsIndex + 1 }}</td>
                            <td>{{ vms.vms.roadNumber }}</td>
                            <td>{{ vms.vms.chainage }}</td>
                            <td>{{ vms.vms.ip }}</td>
                            <td>
                              <!-- Ikona statusu w trzech stanach: dostępny / awaria / niedostępny.
                                   Pełna treść statusu ze sterownika jest w dymku. -->
                              <img
                                :src="statusIcon(vms)"
                                :alt="statusLabel(vms)"
                                :title="vms.vmsStatus.status || statusLabel(vms)"
                              />
                            </td>
                            <td>
                              <!-- Status: czy znak wyświetla treść -->
                              {{ vms.vmsStatus.available ? (vms.vmsStatus.contentDisplayed ? t('yes').toUpperCase() : t('no').toUpperCase()) : t('no-data') }}
                            </td>
                            <td>
                              <!-- Podgląd obrazu wybranego wariantu (jeśli dostępny) -->
                              <img
                                v-if="previewFor(groupId, vms)"
                                :src="previewFor(groupId, vms)"
                                :alt="t('sign-image')"
                                class="img-fluid"
                              />
                              <span v-else>{{ t('no-image') }}</span>
                            </td>
                            <td>{{ selectedVariantFor(groupId) || t('not-selected') }}</td>
                            <!-- Sterowanie pojedynczym znakiem - tylko konta obsługi technicznej (login "_...").
                                 Celowo bez reguły "albo cała grupa, albo nic" i bez sprawdzania dostępności. -->
                            <td v-if="isServiceUser">
                              <div class="single-sign-control">
                                <button
                                  class="vmsOnOffButton enableSingle btn-download"
                                  :disabled="!canEnableSingle(groupId, vms)"
                                  :title="singleEnableBlockReason(groupId, vms) || ''"
                                  @click="toggleSingleSign('on', groupId, vms)"
                                >
                                  {{ t('vms-enable-single') }}
                                </button>
                                <button
                                  class="vmsOnOffButton disableSingle btn-download"
                                  :disabled="isSignBusy(groupId, vms)"
                                  @click="toggleSingleSign('off', groupId, vms)"
                                >
                                  {{ t('vms-disable-single') }}
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>

                    <!-- Panel do wyboru wariantu oraz włącz/wyłącz wszystkie znaki w grupie -->
                    <td class="button-container">
                      <div style="margin-bottom:5px;">{{ t('select-variant') }}</div>
                      <!-- Wybierz wariant treści do wyświetlenia na znakach -->
                      <select class="vmsOnOffButton vmsCheckVariant" style="margin-bottom:5px;" @change="handleVariantSelect($event, groupId)">
                        <option value="0">----</option>
                        <option
                          v-for="contentVariant in getUniqueVariants(group)"
                          :key="contentVariant.id"
                          :value="contentVariant.variant"
                        >
                          {{ contentVariant.variant }}
                        </option>
                      </select>
                      <!-- Przycisk: Włącz wszystkie znaki w grupie -->
                      <button
                        class="vmsOnOffButton enableGroup btn-download"
                        :disabled="!canEnableGroup(groupId)"
                        @click="toggleAllGroupSigns('on', groupId)"
                      >
                        {{ t('enable-all') }}
                      </button>
                      <!-- Powód blokady — operator nie musi zgadywać, czemu przycisk jest szary. -->
                      <div class="block-reason" v-if="enableBlockReason(groupId)">
                        {{ enableBlockReason(groupId) }}
                      </div>
                      <!-- Przycisk: Wyłącz wszystkie znaki w grupie -->
                      <button
                        class="vmsOnOffButton disableGroup btn-download"
                        :disabled="!canDisableGroup(groupId)"
                        @click="toggleAllGroupSigns('off', groupId)"
                      >
                        {{ t('disable-all') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Informacja, gdy nie ma danych -->
    <div class="row vehicle-panel" v-if="!loading && hasNoVisibleGroups">
      <div class="col-3" id="no-data-info">
        <div class="row details-row-field">
          {{ t('no-data-for-selected-station') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Importy bibliotek i komponentów
import Vms, { VMS_STATUS_REFRESH_DELAY_MS } from "@/services/Vms";
import iconAvailable from "@/assets/icons/dostepny.png";
import iconUnavailable from "@/assets/icons/niedostepny.png";
import iconMalfunction from "@/assets/icons/awaria.png";
import iconEnabled from "@/assets/icons/wlaczony.png";
import { useVmsFilterStore } from "@/stores/vmsFilters";
import { useVmsStore } from "@/stores/vms";
import { useUserStore, isServiceUsername } from "@/stores/user.store";
import { useNotificationStore } from "@/stores/notification.store";
import SpinnerElement from "./SpinnerElement.vue";

export default {
  name: "VmsData",
  components: {
    SpinnerElement,
  },
  props: {
    // Przekazywane statusy grup z rodzica
    groupStatuses: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    // Opisy grup (np. nazwy odcinków)
    groupDescriptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    // Stacje preselekcji, do których należą grupy (do filtrowania listy)
    groupLocations: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup() {
    // Pinia store (filtrowanie znaków) oraz store z danymi grup.
    const filtersStore = useVmsFilterStore();
    const vmsStore = useVmsStore();
    const userStore = useUserStore();
    return { filtersStore, vmsStore, userStore };
  },
  data() {
    return {
      loading: false,        // Czy trwa ładowanie danych
      // Jawne decyzje operatora o rozwinięciu grup: { [groupId]: bool }. Grupy, których nie ruszał, rządzą się
      // regułą domyślną (pierwsza rozwinięta) - patrz isExpanded().
      expandedGroups: {},
      // Wybrany wariant komunikatu dla grupy: { [groupId]: numer wariantu }.
      selectedVariants: {},
      // Podglądy obrazów: { [groupId]: { [vmsId]: base64 } }. Trzymane osobno, żeby nie mutować danych ze store'u -
      // wcześniej podgląd był doklejany wprost do obiektów znaków i wracał do stanu globalnego.
      previews: {},
      // Ostatnia operacja wykonana na grupie: { [groupId]: wpis z logu }.
      lastLogs: {},
      // Grupy, dla których trwa operacja sterowania - blokują wtedy własne przyciski.
      busyGroups: {},
      // Znaki, dla których trwa serwisowa operacja sterowania pojedynczym znakiem: { [vmsId]: true }.
      busySigns: {},
      // Zaplanowane odświeżenia grup, w których znaki zmieniają treść: { [groupId]: id timera }.
      changeRefreshTimers: {},
    };
  },
  computed: {
    /**
     * Grupy widoczne na liście - z propsów (gdy rodzic je przekazał) albo wprost ze store'u.
     */
    allGroups() {
      return Object.keys(this.groupStatuses).length ? this.groupStatuses : this.vmsStore.groupStatuses;
    },
    descriptions() {
      return Object.keys(this.groupDescriptions).length ? this.groupDescriptions : this.vmsStore.groupDescriptions;
    },
    locations() {
      return Object.keys(this.groupLocations).length ? this.groupLocations : this.vmsStore.groupLocations;
    },
    /**
     * Grupy po zastosowaniu filtra stacji z panelu bocznego.
     */
    visibleGroups() {
      const selected = this.filtersStore.filters.locations;

      if (!selected?.length) {
        return this.allGroups;
      }

      const filtered = {};

      Object.entries(this.allGroups).forEach(([groupId, group]) => {
        if (selected.includes(this.locations[groupId]?.id)) {
          filtered[groupId] = group;
        }
      });

      return filtered;
    },
    /**
     * Czy lista jest pusta - albo w systemie nie ma grup, albo odfiltrowała je wybrana stacja.
     */
    hasNoVisibleGroups() {
      return !Object.keys(this.visibleGroups).length;
    },
    /**
     * Czy zalogowany jest użytkownik obsługi technicznej - tylko on widzi sterowanie pojedynczym znakiem.
     * Backend pilnuje tego niezależnie (403 dla pozostałych kont).
     */
    isServiceUser() {
      return isServiceUsername(this.userStore.currentUser?.username);
    },
  },
  methods: {
    /**
     * Pobiera dane grup, gdy rodzic nic nie przekazał (np. wejście prosto na /vms albo odświeżenie strony).
     */
    async loadDataIfPropsEmpty() {
      this.loading = true;
      try {
        await this.vmsStore.loadData();
      } catch (err) {
        console.error("Błąd przy automatycznym pobieraniu danych:", err);
        this.showStatus(this.t('vms-load-error'), "error");
      } finally {
        // Pusta lista pokazuje komunikat o braku danych - stan błędu nie wymaga osobnej flagi.
        this.loading = false;
      }
    },
    /**
     * Czy grupa jest rozwinięta.
     *
     * Domyślnie rozwinięta jest pierwsza grupa na liście, ale decyzja operatora ma pierwszeństwo i jest zapamiętana.
     * Do 2026-09-24 stan rozwinięcia był przeliczany od nowa po każdej operacji sterowania, przez co kliknięcie
     * "Włącz" w trzeciej grupie zwijało widok z powrotem do pierwszej.
     */
    isExpanded(groupId) {
      const key = String(groupId);

      if (key in this.expandedGroups) {
        return this.expandedGroups[key];
      }

      return Object.keys(this.visibleGroups)[0] === key;
    },
    /**
     * Czy grupę można zapalić.
     *
     * Obowiązuje zasada "albo cała grupa, albo nic": komunikat musi być wybrany, każdy znak musi go mieć
     * w swoich wariantach i każdy znak musi odpowiadać. Częściowo zapalony odcinek jest gorszy niż odcinek ciemny -
     * część kierowców widzi wtedy komunikat, a część nie.
     */
    canEnableGroup(groupId) {
      return !this.busyGroups[groupId] && this.enableBlockReason(groupId) === null;
    },
    /**
     * Czy grupę można wygasić.
     *
     * Gaszenie prowadzi w stronę stanu bezpieczniejszego, więc nie wymaga ani wybranego wariantu, ani dostępności
     * wszystkich znaków - gdy znak padnie po zapaleniu grupy, operator MUSI móc zgasić pozostałe. Blokuje je
     * wyłącznie trwająca zmiana treści: zapis w trakcie obrotu trafiłby w zajęty sterownik i kończył się
     * komunikatem o częściowym niepowodzeniu, a drugie kliknięcie (po obrocie) - komunikatem o sukcesie.
     */
    canDisableGroup(groupId) {
      return !this.busyGroups[groupId] && this.disableBlockReason(groupId) === null;
    },
    /**
     * Zwraca powód, dla którego nie da się zgasić grupy, albo NULL gdy nic nie stoi na przeszkodzie.
     */
    disableBlockReason(groupId) {
      const group = this.visibleGroups[groupId] || [];

      if (!group.length) {
        return this.t('vms-no-signs-in-group');
      }

      // Znaki, które jeszcze zmieniają treść (np. obracają pryzmy) po poprzedniej operacji - zapis w tym czasie
      // trafiłby w zajęty sterownik.
      const changing = group.filter(vms => vms.vmsStatus?.changeInProgressUntil);

      if (changing.length) {
        return this.t('vms-change-in-progress', { signs: this.formatSigns(changing) });
      }

      return null;
    },
    /**
     * Zwraca powód, dla którego nie da się zapalić grupy, albo NULL gdy nic nie stoi na przeszkodzie.
     *
     * Powód jest pokazywany pod przyciskiem - operator nie musi zgadywać, czemu przycisk jest szary.
     */
    enableBlockReason(groupId) {
      const group = this.visibleGroups[groupId] || [];
      const disableReason = this.disableBlockReason(groupId);

      if (disableReason !== null) {
        return disableReason;
      }

      // Znaki, które nie odpowiadają - reguła "albo cała grupa, albo nic": żaden znak nie może zostać zapalony,
      // gdy choć jeden z grupy jest niedostępny.
      const unavailable = group.filter(vms => !vms.vmsStatus?.available);

      if (unavailable.length) {
        return this.t('vms-signs-unavailable', { signs: this.formatSigns(unavailable) });
      }

      const variant = this.selectedVariants[groupId];

      if (!variant) {
        return this.t('vms-select-variant-first');
      }

      // Znaki, które nie mają tej treści w swoich wariantach - backend odrzuciłby dla nich operację.
      const withoutVariant = group.filter(vms => !this.findVariant(vms, variant));

      if (withoutVariant.length) {
        return this.t('vms-variant-missing-on-signs', { signs: this.formatSigns(withoutVariant) });
      }

      return null;
    },
    /**
     * Planuje odświeżenie grupy na moment, w którym jej znaki skończą zmieniać treść.
     *
     * Do tego czasu przyciski są zablokowane, a backend zwraca stan docelowy operacji - bez odświeżenia operator
     * musiałby klikać "odśwież", żeby odblokować sterowanie.
     */
    scheduleRefreshAfterChange(groupId) {
      if (this.changeRefreshTimers[groupId]) {
        return;
      }

      const delays = (this.allGroups[groupId] || [])
        .filter(vms => vms.vmsStatus?.changeInProgressUntil)
        .map(vms => {
          const until = new Date(vms.vmsStatus.changeInProgressUntil).getTime();
          // Oba znaczniki pochodzą z serwera - różnica nie zależy od rozjazdu zegara przeglądarki.
          const checkedAt = vms.vmsStatus.checkedAt ? new Date(vms.vmsStatus.checkedAt).getTime() : Date.now();
          return until - checkedAt;
        })
        .filter(delay => !Number.isNaN(delay));

      if (!delays.length) {
        return;
      }

      // Margines na zaokrąglenia i czas odpowiedzi - odpytany za wcześnie znak wciąż byłby "w trakcie zmiany".
      const delay = Math.max(...delays, 0) + 1000;

      this.changeRefreshTimers[groupId] = setTimeout(async () => {
        delete this.changeRefreshTimers[groupId];
        await this.refreshGroup(groupId);
      }, delay);
    },
    /**
     * Wykonuje operację sterowania całą grupą i odświeża jej stan.
     *
     * @param {String} action 'on' zapala grupę, 'off' ją gasi.
     * @param {Number|String} groupId Identyfikator grupy.
     */
    async toggleAllGroupSigns(action, groupId) {
      // DEBUG VMS
      console.log('[VMS] toggleAllGroupSigns', {
        action,
        groupId,
        variant: this.selectedVariants[groupId],
        canEnable: this.canEnableGroup(groupId),
        blockReason: this.enableBlockReason(groupId),
        signs: this.visibleGroups[groupId],
      });
      this.busyGroups = { ...this.busyGroups, [groupId]: true };

      try {
        let result;

        if (action === 'on') {
          result = await Vms.enableVms(groupId, this.selectedVariants[groupId]);
          this.reportEnableResult(result);
        } else {
          result = await Vms.disableVms(groupId);
          this.reportDisableResult(result);
        }
      } catch (err) {
        console.error('Błąd przy przełączaniu znaków:', err);
        console.log('[VMS] toggleAllGroupSigns BŁĄD (typ: ' + typeof err + '):', err);
        // Backend zwraca 409 z gotowym komunikatem, gdy któryś znak jest niedostępny - pokazujemy go wprost.
        this.showStatus(typeof err === 'string' ? err : this.t('vms-operation-error'), 'error');
      } finally {
        // Sterowniki znaków potrzebują chwili na aktualizację swojego stanu - odpytane za wcześnie zwrócą starą
        // treść. Do 2026-09-24 odczekiwał backend, blokując przy tym workera PHP-FPM.
        console.log('[VMS] odświeżenie grupy', groupId, 'za', VMS_STATUS_REFRESH_DELAY_MS, 'ms');
        setTimeout(() => this.refreshGroup(groupId), VMS_STATUS_REFRESH_DELAY_MS);
      }
    },
    /**
     * Informuje operatora o wyniku zapalania grupy.
     */
    reportEnableResult(result) {
      if (!Vms.hasFailures(result)) {
        this.showStatus(this.t('vms-enable-success'), 'success');
        return;
      }

      // Zapalanie NIE jest wycofywane - pozostałe znaki wyświetlają komunikat, a operator decyduje, czy zgasić
      // grupę przyciskiem "Wyłącz wszystkie".
      this.showStatus(
        this.t('vms-enable-partial', { signs: Vms.formatSignList(result.failed) }),
        'error'
      );
    },
    /**
     * Informuje operatora o wyniku gaszenia grupy.
     */
    reportDisableResult(result) {
      if (!Vms.hasFailures(result)) {
        this.showStatus(this.t('vms-disable-success'), 'success');
        return;
      }

      this.showStatus(
        this.t('vms-disable-partial', { signs: Vms.formatSignList(result.failed) }),
        'warning'
      );
    },
    /**
     * Czy trwa operacja na znaku - serwisowa na nim samym albo na całej jego grupie.
     */
    isSignBusy(groupId, vms) {
      return Boolean(this.busySigns[vms.vms.id] || this.busyGroups[groupId]);
    },
    /**
     * Zwraca powód, dla którego nie da się zapalić pojedynczego znaku, albo NULL gdy nic nie stoi na przeszkodzie.
     *
     * Sterowanie serwisowe celowo pomija dostępność znaku i trwającą zmianę treści - wymagany jest wyłącznie
     * wariant, który znak ma w swoich treściach.
     */
    singleEnableBlockReason(groupId, vms) {
      const variant = this.selectedVariants[groupId];

      if (!variant) {
        return this.t('vms-select-variant-first');
      }

      if (!this.findVariant(vms, variant)) {
        return this.t('vms-variant-missing-on-signs', { signs: this.formatSigns([vms]) });
      }

      return null;
    },
    /**
     * Czy pojedynczy znak można zapalić (sterowanie serwisowe).
     */
    canEnableSingle(groupId, vms) {
      return !this.isSignBusy(groupId, vms) && this.singleEnableBlockReason(groupId, vms) === null;
    },
    /**
     * Wykonuje serwisową operację sterowania pojedynczym znakiem i odświeża stan jego grupy.
     *
     * @param {String} action 'on' zapala znak wariantem wybranym dla grupy, 'off' go gasi.
     * @param {Number|String} groupId Identyfikator grupy, w której operator kliknął przycisk.
     * @param {Object} vms Znak w kształcie zwracanym przez /api/v1/vms-groups-status.
     */
    async toggleSingleSign(action, groupId, vms) {
      const vmsId = vms.vms.id;
      const sign = this.formatSigns([vms]);
      this.busySigns = { ...this.busySigns, [vmsId]: true };

      try {
        if (action === 'on') {
          const result = await Vms.enableSingleVms(vmsId, this.selectedVariants[groupId]);
          this.reportSingleResult(result, sign, 'vms-enable-single-success', 'vms-enable-single-failed');
        } else {
          const result = await Vms.disableSingleVms(vmsId);
          this.reportSingleResult(result, sign, 'vms-disable-single-success', 'vms-disable-single-failed');
        }
      } catch (err) {
        console.error('Błąd przy sterowaniu pojedynczym znakiem:', err);
        this.showStatus(typeof err === 'string' ? err : this.t('vms-operation-error'), 'error');
      } finally {
        // Jak przy sterowaniu grupą - sterownik potrzebuje chwili na aktualizację swojego stanu.
        setTimeout(async () => {
          await this.refreshGroup(groupId);
          this.busySigns = { ...this.busySigns, [vmsId]: false };
        }, VMS_STATUS_REFRESH_DELAY_MS);
      }
    },
    /**
     * Informuje operatora o wyniku sterowania pojedynczym znakiem.
     */
    reportSingleResult(result, sign, successKey, failedKey) {
      if (!Vms.hasFailures(result)) {
        this.showStatus(this.t(successKey, { sign }), 'success');
        return;
      }

      this.showStatus(this.t(failedKey, { sign, error: result.failed[0]?.error || '—' }), 'error');
    },
    /**
     * Odświeża stan znaków w jednej grupie oraz informację o ostatniej operacji.
     */
    async refreshGroup(groupId) {
      const refreshed = await this.vmsStore.refreshGroup(groupId);

      if (!refreshed) {
        this.showStatus(this.t('vms-refresh-error'), 'error');
      }

      // Podglądy dotyczą wariantu wybranego przed operacją - po odświeżeniu danych zostają, bo wybór operatora
      // się nie zmienił.
      this.busyGroups = { ...this.busyGroups, [groupId]: false };

      this.loadLastLog(groupId);
    },
    /**
     * Rozwiń/zwiń daną grupę.
     */
    toggleGroup(groupId) {
      const expanded = !this.isExpanded(groupId);
      this.expandedGroups = { ...this.expandedGroups, [String(groupId)]: expanded };

      // Informację o ostatniej operacji pobieramy dopiero przy rozwinięciu - dla zwiniętych grup nie jest widoczna.
      if (expanded && this.lastLogs[groupId] === undefined) {
        this.loadLastLog(groupId);
      }
    },
    /**
     * Pobiera informację o ostatniej operacji sterowania wykonanej na grupie.
     */
    async loadLastLog(groupId) {
      const log = await Vms.getGroupLastLog(groupId);
      this.lastLogs = { ...this.lastLogs, [groupId]: log || null };
    },
    /**
     * Zwraca listę unikalnych wariantów (treści) możliwych do wyświetlenia w grupie.
     */
    getUniqueVariants(group) {
      const seenVariants = new Set();
      const uniqueVariants = [];
      group.forEach(vms => {
        (vms.contentVariants || []).forEach(variant => {
          const variantValue = variant?.variant;
          if (variantValue && !seenVariants.has(variantValue)) {
            seenVariants.add(variantValue);
            uniqueVariants.push(variant);
          }
        });
      });
      return uniqueVariants.sort((a, b) => a.variant - b.variant);
    },
    /**
     * Szuka wariantu o zadanym numerze wśród treści przypisanych do znaku.
     */
    findVariant(vms, variant) {
      return (vms.contentVariants || []).find(cv => cv.variant === variant) || null;
    },
    /**
     * Obsługuje wybór wariantu komunikatu dla grupy - pobiera podglądy obrazów dla wszystkich znaków.
     *
     * To wyłącznie podgląd w przeglądarce, na znaki nic jeszcze nie idzie.
     */
    async handleVariantSelect(event, groupId) {
      const variant = Number(event.target.value);
      const group = this.visibleGroups[groupId] || [];
      // DEBUG VMS
      console.log('[VMS] handleVariantSelect', {
        groupId,
        variant,
        signs: group.map(vms => ({ id: vms.vms?.id, variants: vms.contentVariants, match: this.findVariant(vms, variant) })),
      });

      // Wartość 0 oznacza brak wyboru.
      if (!variant) {
        this.selectedVariants = { ...this.selectedVariants, [groupId]: null };
        this.previews = { ...this.previews, [groupId]: {} };
        return;
      }

      this.selectedVariants = { ...this.selectedVariants, [groupId]: variant };

      const groupPreviews = {};

      for (const vms of group) {
        const matchingVariant = this.findVariant(vms, variant);

        groupPreviews[vms.vms.id] = matchingVariant?.content?.id
          ? await this.fetchImageForSign(matchingVariant.content.id)
          : null;
      }

      this.previews = { ...this.previews, [groupId]: groupPreviews };
    },
    /**
     * Zwraca podgląd obrazu dla znaku albo NULL, gdy wariant nie został wybrany.
     */
    previewFor(groupId, vms) {
      return this.previews[groupId]?.[vms.vms.id] || null;
    },
    /**
     * Zwraca numer wariantu wybranego dla grupy (do kolumny "wybrany wariant").
     */
    selectedVariantFor(groupId) {
      return this.selectedVariants[groupId] || null;
    },
    /**
     * Pobierz obraz pod wariantem (do podglądu).
     */
    async fetchImageForSign(id) {
      try {
        const imageBase64 = await Vms.getImageForSign(id);
        return imageBase64 ? imageBase64 : null;
      } catch (error) {
        console.error('Błąd podczas pobierania obrazu:', error);
        this.showStatus(this.t('vms-image-error'), 'error');
        return null;
      }
    },
    /**
     * Zwraca ikonę statusu znaku.
     *
     * Cztery stany, zgodnie z legendą w panelu bocznym:
     *  - włączony           - znak sprawny ('OK') i wyświetla treść,
     *  - dostępny (zielony) - WYŁĄCZNIE gdy sterownik zwrócił status 'OK' i znak nie wyświetla treści,
     *  - awaria (czerwony)  - znak odpowiada, ale zgłasza cokolwiek innego niż 'OK', np.
     *                         "Sign set to manual mode, local control activated" albo
     *                         "Safety mode activated, motor stoped, sign error",
     *  - niedostępny        - brak komunikacji ze znakiem.
     *
     * Wcześniej wszystko, co odpowiadało, dostawało ikonę "dostępny" - znak w trybie lokalnym albo
     * z zatrzymanym silnikiem wyglądał w tabeli identycznie jak sprawny.
     */
    statusIcon(vms) {
      if (!vms.vmsStatus?.available) {
        return iconUnavailable;
      }

      if (vms.vmsStatus.status !== 'OK') {
        return iconMalfunction;
      }

      return vms.vmsStatus.contentDisplayed ? iconEnabled : iconAvailable;
    },
    /**
     * Opis statusu znaku - trafia do atrybutu `alt` oraz do dymka, gdy sterownik nie podał treści statusu.
     */
    statusLabel(vms) {
      if (!vms.vmsStatus?.available) {
        return this.t('unavailable');
      }

      if (vms.vmsStatus.status !== 'OK') {
        return this.t('malfunction');
      }

      return vms.vmsStatus.contentDisplayed ? this.t('enabled') : this.t('available');
    },
    /**
     * Skleja opis znaków w formie czytelnej dla operatora, np. "A4/126+300, A4/128+100".
     */
    formatSigns(signs) {
      return signs.map(item => `${item.vms.roadNumber}/${item.vms.chainage}`).join(', ');
    },
    /**
     * Zwraca moment ostatniego odpytania znaków w grupie - najstarszy z odczytów, bo to on decyduje o tym,
     * jak świeży jest obraz całej grupy.
     */
    checkedAtFor(groupId) {
      const timestamps = (this.visibleGroups[groupId] || [])
        .map(vms => vms.vmsStatus?.checkedAt)
        .filter(Boolean)
        .map(value => new Date(value).getTime())
        .filter(time => !Number.isNaN(time));

      return timestamps.length ? new Date(Math.min(...timestamps)) : null;
    },
    /**
     * Formatuje moment odczytu do postaci godzinowej.
     */
    formatTime(date) {
      if (!date) {
        return '';
      }

      // Locale zostawiamy przeglądarce - godzina w formacie 2-cyfrowym wygląda tak samo we wszystkich
      // językach obsługiwanych przez aplikację, więc nie ma po co wiązać tego z ustawieniem i18n.
      return new Date(date).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    /**
     * Opisuje ostatnią operację wykonaną na grupie, np. "Włączenie · jkowalski · 14:32".
     */
    lastLogLabel(groupId) {
      const log = this.lastLogs[groupId];

      if (!log) {
        return '';
      }

      const action = log.eventType === 'enable' ? this.t('vms-action-enable') : this.t('vms-action-disable');

      return `${action} · ${log.user?.username || ''} · ${this.formatTime(log.createdAt)}`;
    },
    // Wynik operacji pokazuje globalny komponent powiadomień (AppNotifications).
    showStatus(message, type = "error") {
      useNotificationStore().push(message, type);
    },
  },
  watch: {
    // Po każdej zmianie danych (załadowanie widoku, odświeżenie po operacji) sprawdzamy, czy któraś grupa
    // nie czeka na zakończenie zmiany treści na znakach.
    allGroups: {
      immediate: true,
      handler(groups) {
        Object.keys(groups || {}).forEach(groupId => this.scheduleRefreshAfterChange(groupId));
      },
    },
  },
  beforeUnmount() {
    Object.values(this.changeRefreshTimers).forEach(timer => clearTimeout(timer));
    this.changeRefreshTimers = {};
  },
  mounted() {
    // DEBUG VMS
    console.log('[VMS] Vms.vue mounted', {
      API_URL: process.env.VUE_APP_API_URL,
      VERSION: process.env.VUE_APP_VERSION_NAME,
      REFRESH_DELAY_MS: VMS_STATUS_REFRESH_DELAY_MS,
      propsGroups: Object.keys(this.groupStatuses).length,
    });
    // Jeżeli nie przekazano propsów - pobierz dane z API.
    if (!Object.keys(this.groupStatuses).length) {
      this.loadDataIfPropsEmpty();
    }
  },
};
</script>


<style scoped>
.row {
  margin: 0;
}
.content {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-lg);
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-panel);
  margin-bottom: 1rem;
}

.table-container button,
.button-container button.vmsOnOffButton,
.single-sign-control button.vmsOnOffButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  min-height: var(--control-height-sm);
  padding: 0 var(--spacing-lg);
  background-color: var(--surface-primary);
  border: 1px solid var(--border-strong);
  border-radius: var(--control-radius);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  cursor: pointer;
  text-align: center;
}

/* Nagłówek grupy: nazwa odcinka + metadane o świeżości stanu i ostatniej operacji. */
.group-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.group-meta {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: var(--font-weight-regular);
  letter-spacing: normal;
  text-transform: none;
}

/* Powód zablokowania przycisku "Włącz wszystkie". Bez niego operator widzi tylko szary przycisk. */
.block-reason {
  width: 75%;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.3;
}

h5 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--text-primary);
}

.details-row-field {
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-md);
}

.details-row-field-b {
  font-weight: var(--font-weight-bold);
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-size: var(--font-size-md);
}
.vehicle-panel {
  margin-top: 15px;
}
#road-info .row > div:first-child {
  border: 0;
}

table {
  width: 100%;
}

thead, tbody {
  width: 100%;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.vmsOnOffButton {
  width: 75%;
}

.lp {
  width: 5%;
}

.road_no {
  width: 5%;
}

.chainage {
  width: 10%;
}

.ip_address {
  width: 20%;
}

.status {
  width: 10%;
}

.sign_enabled {
  width: 10%;
}

.sign_image {
  width: 15%;
}

.variant {
  width: 15%;
}

.service_control {
  width: 24%;
}

/* Z kolumną sterowania serwisowego pozostałe kolumny oddają jej miejsce - suma szerokości musi zostać 100%,
   inaczej tabela rozpycha panel grupy i kolumna z wyborem wariantu wyjeżdża poza ramkę. */
.with-service-control .lp { width: 4%; }
.with-service-control .road_no { width: 6%; }
.with-service-control .chainage { width: 9%; }
.with-service-control .ip_address { width: 13%; }
.with-service-control .status { width: 7%; }
.with-service-control .sign_enabled { width: 9%; }
.with-service-control .sign_image { width: 16%; }
.with-service-control .variant { width: 12%; }

/* Przyciski serwisowe w wierszu znaku - jeden pod drugim. Flex siedzi na wewnętrznym kontenerze, nie na <td>:
   komórka z `display: flex` przestaje być komórką tabeli i rozjeżdża obramowania wiersza. */
.single-sign-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
}

/* Metryki jak "Włącz wszystkie" / "Wyłącz wszystkie" - tamte biorą wielkie litery z `.enableGroup` /
   `.disableGroup` w button-system.css, tych klas nie można tu użyć (selektory testów i kolory). */
.single-sign-control button.vmsOnOffButton {
  width: 100%;
  padding: var(--spacing-xs) var(--spacing-sm);
  text-transform: uppercase;
  line-height: var(--line-height-tight);
  white-space: normal;
}

.btn-download {
  border-radius: 4px;
}

.btn-download:hover {
  background-color: var(--border-default) !important;
}
</style>
